import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { MapPin, Bell, Check, X, Sparkles, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha } from "@/lib/format";
import { useEmpresa } from "@/hooks/useEmpresa";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SolicitudCambioZonaDialog from "@/components/SolicitudCambioZonaDialog";
import { workerZonasAccentStyle, workerZonasHeaderStyle, workerZonasSubtitleStyle } from "@/lib/uiTheme";

const ETIQUETA_NOVEDAD = { creada: "Nuevo", modificada: "Modificado" };

function BadgeNovedad({ tipo }) {
  if (!tipo) return null;
  const esNuevo = tipo === "creada";
  return (
    <Badge className={esNuevo ? "bg-emerald-600 hover:bg-emerald-600" : "bg-amber-600 hover:bg-amber-600"}>
      {ETIQUETA_NOVEDAD[tipo] || "Cambio"}
    </Badge>
  );
}

function FilaCambio({ c }) {
  const Icon = c.tipo === "eliminada" ? Trash2 : c.tipo === "modificada" ? Pencil : Sparkles;
  const color =
    c.tipo === "eliminada" ? "border-rose-200 bg-rose-50" :
    c.tipo === "modificada" ? "border-amber-200 bg-amber-50" :
    "border-emerald-200 bg-emerald-50";
  return (
    <li className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 text-sm ${color}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0 opacity-70" />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-zinc-900">
          {c.tipo === "creada" && "Nueva asignación"}
          {c.tipo === "modificada" && "Asignación modificada"}
          {c.tipo === "eliminada" && "Asignación eliminada"}
          {" · "}
          {fmtFecha(c.fecha)}
        </p>
        <p className="text-zinc-700">
          <span className="font-medium" style={{ color: c.zona_color }}>{c.zona_nombre}</span>
          {c.hora_inicio && (
            <span className="ml-2 text-violet-700">{c.hora_inicio} – {c.hora_fin}</span>
          )}
        </p>
        {c.tipo === "modificada" && c.datos_anteriores && (
          <p className="mt-1 text-xs text-zinc-500">
            Antes: {c.datos_anteriores.zona_nombre} {c.datos_anteriores.hora_inicio}–{c.datos_anteriores.hora_fin}
          </p>
        )}
        {c.notas && <p className="mt-1 text-xs text-zinc-500">{c.notas}</p>}
      </div>
    </li>
  );
}

function TarjetaDia({ fecha, tramos, onSolicitar }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
      <p className="text-sm font-semibold text-zinc-800">{fmtFecha(fecha)}</p>
      <ul className="mt-3 space-y-2">
        {tramos.map((t) => (
          <li
            key={t.id}
            className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 ${
              t.es_novedad ? "border-violet-300 bg-violet-50/60 ring-1 ring-violet-200" : "border-zinc-100"
            }`}
            style={{ borderLeftWidth: 4, borderLeftColor: t.zona_color }}
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-zinc-900">{t.zona_nombre}</p>
                {t.es_novedad && <BadgeNovedad tipo={t.tipo_novedad} />}
              </div>
              <p className="text-sm text-violet-700">{t.hora_inicio} – {t.hora_fin}</p>
              {t.notas && <p className="mt-1 text-xs text-zinc-500">{t.notas}</p>}
            </div>
            <Button size="sm" variant="outline" className="h-8 shrink-0 text-xs" onClick={() => onSolicitar(t)}>
              Cambio
            </Button>
            <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: t.zona_color }} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function MisZonas() {
  const { empresa, cargando } = useEmpresa();
  const [data, setData] = useState(null);
  const [solicitudOpen, setSolicitudOpen] = useState(false);
  const [asignacionSel, setAsignacionSel] = useState(null);
  const [misSolicitudes, setMisSolicitudes] = useState([]);
  const [confirmando, setConfirmando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [d, sol] = await Promise.all([
        api.get("/zonas/mis"),
        api.get("/zonas/solicitudes-cambio").catch(() => ({ data: [] })),
      ]);
      setData(d.data);
      setMisSolicitudes(sol.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const confirmarCambios = async () => {
    setConfirmando(true);
    try {
      await api.post("/zonas/cambios/confirmar");
      toast.success("Has confirmado que has visto los cambios en tu planificación");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirmando(false);
    }
  };

  if (!cargando && empresa && !empresa.gestionar_zonas) {
    return <Navigate to="/mis-solicitudes" replace />;
  }

  const abrirSolicitud = (tramo = null) => {
    setAsignacionSel(tramo);
    setSolicitudOpen(true);
  };

  if (!data) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600" />
      </div>
    );
  }

  const dias = Object.keys(data.por_dia || {}).sort();
  const hoy = new Date().toLocaleDateString("sv");
  const hoyTramos = data.por_dia?.[hoy] || [];
  const pendientes = misSolicitudes.filter((s) => ["pendiente_pareja", "pendiente_encargado"].includes(s.estado));
  const pendientesPareja = misSolicitudes.filter((s) => s.requiere_mi_respuesta);
  const cambiosPendientes = data.cambios_pendientes || [];

  const responderPareja = async (s, acepta) => {
    try {
      await api.put(`/intercambios/zona/${s.id}/responder-pareja`, { acepta, comentario: "" });
      toast.success(acepta ? "Has aceptado el intercambio" : "Has rechazado el intercambio");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="mis-zonas-page">
      <div className="rounded-2xl border p-4" style={workerZonasHeaderStyle()}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl text-white" style={workerZonasAccentStyle()}>
              <MapPin className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-semibold text-zinc-900">Zonas</h1>
              <p className="text-sm" style={workerZonasSubtitleStyle()}>Dónde y cuándo trabajas cada día en el establecimiento</p>
            </div>
          </div>
          <Button onClick={() => abrirSolicitud()} className="border-0 hover:opacity-90" style={workerZonasAccentStyle()}>
            Solicitar cambio
          </Button>
        </div>
      </div>

      {data.requiere_confirmacion && cambiosPendientes.length > 0 && (
        <div className="rounded-xl border-2 border-violet-400 bg-violet-50 p-4" data-testid="cambios-pendientes-panel">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-violet-900">
                Tienes {cambiosPendientes.length} cambio{cambiosPendientes.length !== 1 ? "s" : ""} en tu planificación
              </p>
              <p className="mt-1 text-xs text-violet-800/80">
                Revisa los cambios acumulados y confirma que los has visto. Los tramos nuevos o modificados aparecen resaltados abajo.
              </p>
            </div>
            <Button
              className="shrink-0 gap-1.5 bg-violet-700 hover:bg-violet-800"
              onClick={confirmarCambios}
              disabled={confirmando}
              data-testid="confirmar-cambios-btn"
            >
              <Check className="h-4 w-4" />
              {confirmando ? "Confirmando…" : "He visto los cambios"}
            </Button>
          </div>
          <ul className="mt-4 max-h-64 space-y-2 overflow-y-auto">
            {cambiosPendientes.map((c) => (
              <FilaCambio key={c.id} c={c} />
            ))}
          </ul>
        </div>
      )}

      {pendientesPareja.length > 0 && (
        <div className="rounded-xl border-2 border-violet-300 bg-violet-50 px-4 py-3">
          <p className="text-sm font-semibold text-violet-900">Te han solicitado un intercambio</p>
          {pendientesPareja.map((s) => (
            <div key={s.id} className="mt-2 rounded-lg border border-violet-200 bg-white p-3 text-sm">
              <p className="font-medium">{s.usuario} solicita intercambiar contigo</p>
              <p className="text-zinc-500">{s.motivo}</p>
              <div className="mt-2 flex gap-2">
                <Button size="sm" className="h-8 gap-1 bg-green-600" onClick={() => responderPareja(s, true)}><Check className="h-3.5 w-3.5" /> Aceptar</Button>
                <Button size="sm" variant="outline" className="h-8 gap-1 border-red-200 text-red-600" onClick={() => responderPareja(s, false)}><X className="h-3.5 w-3.5" /> Rechazar</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pendientes.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Tienes {pendientes.length} solicitud{pendientes.length !== 1 ? "es" : ""} de cambio en curso.
        </div>
      )}

      {hoyTramos.length > 0 ? (
        <div className="rounded-xl border-2 border-violet-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-violet-600">Hoy</p>
          <ul className="mt-2 space-y-2">
            {hoyTramos.map((t) => (
              <li
                key={t.id}
                className={`flex items-center justify-between gap-2 rounded-lg px-2 py-2 ${
                  t.es_novedad ? "ring-2 ring-violet-300" : ""
                }`}
                style={{ backgroundColor: `${t.zona_color}18` }}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium" style={{ color: t.zona_color }}>{t.zona_nombre}</span>
                  <Badge variant="outline">{t.hora_inicio} – {t.hora_fin}</Badge>
                  {t.es_novedad && <BadgeNovedad tipo={t.tipo_novedad} />}
                </div>
                <Button size="sm" variant="outline" className="h-8" onClick={() => abrirSolicitud(t)}>Cambio</Button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50 px-4 py-6 text-center text-sm text-zinc-500">
          No tienes zonas asignadas para hoy.
        </p>
      )}

      {dias.length > 0 ? (
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Próximos días</p>
          {dias.filter((d) => d !== hoy).map((fecha) => (
            <TarjetaDia key={fecha} fecha={fecha} tramos={data.por_dia[fecha]} onSolicitar={abrirSolicitud} />
          ))}
        </div>
      ) : (
        !hoyTramos.length && (
          <p className="text-center text-sm text-zinc-400 py-4">Sin asignaciones en las próximas dos semanas.</p>
        )
      )}

      <p className="flex items-center gap-2 text-xs text-zinc-400">
        <Bell className="h-3.5 w-3.5" />
        Recibirás un aviso cuando se publiquen cambios en tu planificación. Confírmalos en esta pantalla para que el encargado sepa que los has visto.
      </p>

      <SolicitudCambioZonaDialog
        open={solicitudOpen}
        onOpenChange={setSolicitudOpen}
        asignacionInicial={asignacionSel}
        onSuccess={cargar}
      />
    </div>
  );
}
